const heroHighlights = [
  { label: "Placement offers", value: "92%", helper: "Avg. conversion across last 3 cohorts" },
  { label: "Live cohorts", value: "12", helper: "Product, Cloud, and Fullstack tracks" },
  { label: "Trainer rating", value: "4.8", helper: "Feedback from 1,240 learner reviews" },
];

const featureList = [
  {
    title: "Single source of truth",
    body: "Dashboards for admins, trainers, and trainees keep attendance, tasks, and placements aligned.",
  },
  {
    title: "Outcome-first sprints",
    body: "Weekly rituals pair learning goals with career preparation so cohorts stay industry-ready.",
  },
  {
    title: "Placement concierge",
    body: "Structured shortlists, interview prep kits, and updates that keep trainees confident.",
  },
  {
    title: "Signal-rich insights",
    body: "Progress trends, readiness scores, and pulse surveys to act on engagement early.",
  },
];

const whyUs = [
  { title: "Transparent journeys", body: "Clear steps from induction to offer letter with no blind spots." },
  { title: "Trainer-led clarity", body: "Actionable workboards with tasks, attendance, and nudges in one place." },
  { title: "Placement momentum", body: "Hiring partner-ready profiles, mock interviews, and instant updates." },
];

const placementWins = [
  { value: "140+", label: "Hiring partners" },
  { value: "48 hrs", label: "Average response time to applications" },
  { value: "6 cities", label: "Active placement hubs" },
];

const testimonials = [
  {
    name: "Riya Shah - Trainee",
    copy: "The sprint view and daily check-ins keep me on track without feeling overwhelmed.",
  },
  {
    name: "Sameer Kapoor - Trainer",
    copy: "I can see attendance trends, assign tasks, and prep placement artifacts in one place.",
  },
  {
    name: "Anita Reddy - Admin",
    copy: "We finally run cohorts with predictable visibility on learner readiness and offers.",
  },
];

const Home = () => {
  return (
    <div className="home-page">
      <section className="page-section hero-block">
        <div className="hero-copy">
          <div className="pill">Trainee management - placements</div>
          <h1>Coach every learner with clarity and ship them into great teams.</h1>
          <p>
            A modern workspace for training teams to align attendance, learning milestones, and placement readiness -
            without juggling spreadsheets or inboxes.
          </p>
          <div className="hero-actions">
            <button className="primary">Explore Platform</button>
            <button className="ghost">View Placement Pipeline</button>
          </div>
          <div className="hero-stats">
            {heroHighlights.map((item) => (
              <div key={item.label} className="stat-card">
                <p className="stat-value">{item.value}</p>
                <p className="stat-label">{item.label}</p>
                <p className="muted small">{item.helper}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="hero-visual">
          <div className="glass-card">
            <div className="card-head">
              <p className="eyebrow">Live cohort health</p>
              <span className="badge">Week 5</span>
            </div>
            <ul className="checklist">
              <li>
                <span className="dot positive" />
                Daily attendance above 96%
              </li>
              <li>
                <span className="dot positive" />
                Placement mock interviews scheduled
              </li>
              <li>
                <span className="dot pending" />
                Portfolio reviews due Friday
              </li>
              <li>
                <span className="dot pending" />
                Project showcase rehearsal next week
              </li>
            </ul>
          </div>
          <div className="mini-card-grid">
            <div className="mini-card">
              <p className="muted small">Job-ready score</p>
              <p className="stat-value">87</p>
              <div className="progress">
                <span style={{ width: "87%" }} />
              </div>
            </div>
            <div className="mini-card">
              <p className="muted small">Interviews this week</p>
              <p className="stat-value">24</p>
              <p className="small">Backend | Frontend | Cloud</p>
            </div>
            <div className="mini-card accent">
              <p className="muted small">Placement spotlight</p>
              <p className="stat-value">Finix Labs</p>
              <p className="small">Fullstack Engineer | 10.2 LPA</p>
            </div>
          </div>
        </div>
      </section>

      <section className="page-section feature-section">
        <div className="section-header">
          <p className="eyebrow">Highlights</p>
          <h2>Everything your cohort needs in one clean flow.</h2>
          <p className="muted">No clutter, only the essentials that keep teams focused on outcomes.</p>
        </div>
        <div className="card-grid feature-grid">
          {featureList.map((item) => (
            <article key={item.title} className="feature-card">
              <h3>{item.title}</h3>
              <p>{item.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="page-section why-section">
        <div className="section-header">
          <p className="eyebrow">Why choose us</p>
          <h2>Built for training teams that care about placement outcomes.</h2>
        </div>
        <div className="card-grid trio">
          {whyUs.map((item) => (
            <article key={item.title} className="pill-card">
              <h3>{item.title}</h3>
              <p>{item.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="page-section placement-highlight">
        <div className="section-header">
          <p className="eyebrow">Placements</p>
          <h2>Momentum your hiring partners can feel.</h2>
        </div>
        <div className="placement-metrics">
          {placementWins.map((item) => (
            <div key={item.label} className="metric-card">
              <p className="stat-value">{item.value}</p>
              <p className="stat-label">{item.label}</p>
            </div>
          ))}
        </div>
        <div className="timeline">
          <div className="step">
            <span className="dot positive" />
            <div>
              <h4>Curated shortlists</h4>
              <p>Role-mapped batches with ready project portfolios.</p>
            </div>
          </div>
          <div className="step">
            <span className="dot positive" />
            <div>
              <h4>Prep in public</h4>
              <p>Mock interviews, feedback loops, and readiness signals shared with trainees.</p>
            </div>
          </div>
          <div className="step">
            <span className="dot positive" />
            <div>
              <h4>Offer support</h4>
              <p>Offer tracking, onboarding nudges, and day-one readiness checklists.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="page-section testimonials">
        <div className="section-header">
          <p className="eyebrow">Testimonials</p>
          <h2>Teams stay confident because everyone can see the progress.</h2>
        </div>
        <div className="card-grid testimonial-grid">
          {testimonials.map((item) => (
            <article key={item.name} className="testimonial-card">
              <p>"{item.copy}"</p>
              <strong>{item.name}</strong>
            </article>
          ))}
        </div>
      </section>

      <section className="page-section cta-block">
        <div>
          <p className="eyebrow">Ready for the next cohort?</p>
          <h2>Stand up your next batch with a spotless learner experience.</h2>
          <p className="muted">
            We'll help you set up cohorts, tasks, attendance, and placement pipelines in days, not weeks.
          </p>
        </div>
        <div className="cta-actions">
          <button className="primary">Book a walkthrough</button>
          <button className="ghost">Download overview</button>
        </div>
      </section>
    </div>
  );
};

export default Home;
